import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getRankings, getArenaRankings } from '../services/api';
import { BRAND_GREEN, BRAND_GOLD, BRAND_GREEN_LIGHT } from '../theme/sucronTheme';


// --- Cores da Marca ---
const BORDER = '#e5e7eb';

const RankingDetailsPage = () => {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const year = searchParams.get('year') || '2025';
  const navigate = useNavigate();

  const [rankingData, setRankingData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Navigation State (Event/Gender)
  const [availableEvents, setAvailableEvents] = useState({ male: [], female: [] });
  const [currentGender, setCurrentGender] = useState('male'); // Default, will update on load
  
  // Filters State
  const [filters, setFilters] = useState({
    club: 'all',
    nationality: 'all',
    competition: 'all'
  });
  
  // Unique values for filters
  const [uniqueValues, setUniqueValues] = useState({
    clubs: [],
    nationalities: [],
    competitions: []
  });

  // Fetch available events for navigation
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await getArenaRankings();
        if (response.data && response.data.rankings) {
          // Extract unique events from the rankings
          // The API returns { male: [...], female: [...] } where each item has event_id, event_name, distance, etc.
          // We need to group by gender and deduplicate events
          
          const processEvents = (list) => {
            const unique = new Map();
            list.forEach(item => {
              if (!unique.has(item.event_id)) {
                unique.set(item.event_id, {
                  id: item.event_id,
                  name: item.event_name,
                  distance: item.distance,
                  label: `${item.event_name} ${item.distance}m`
                });
              }
            });
            return Array.from(unique.values()).sort((a, b) => {
               // Sort by distance then name
               if (a.distance !== b.distance) return a.distance - b.distance;
               return a.name.localeCompare(b.name);
            });
          };

          setAvailableEvents({
            male: processEvents(response.data.rankings.male),
            female: processEvents(response.data.rankings.female)
          });
        }
      } catch (err) {
        console.error('Erro ao carregar lista de provas:', err);
      }
    };
    fetchEvents();
  }, []);

  useEffect(() => {
    const fetchRanking = async () => {
      try {
        setLoading(true);
        // Fetch top 100 results for the full ranking view
        const response = await getRankings(eventId, year, 100);
        
        if (response.data && response.data.success) {
          const data = response.data.rankings;
          setRankingData(data);
          setFilteredData(data);
          
          // Determine current gender based on the first result (assuming single gender per event_id)
          // Note: The API doesn't explicitly return gender in the root, but we can infer or it might be in the row
          // Let's check if we can find the gender from the availableEvents list using eventId
          // This is safer than guessing from names
          
          // Extract unique values for filters
          const clubs = [...new Set(data.map(item => item.club_name).filter(Boolean))].sort();
          const nationalities = [...new Set(data.map(item => item.nationality).filter(Boolean))].sort();
          const competitions = [...new Set(data.map(item => item.competition_name).filter(Boolean))].sort();
          
          setUniqueValues({
            clubs,
            nationalities,
            competitions
          });
        } else {
          setError('Falha ao carregar o ranking.');
        }
      } catch (err) {
        console.error('Erro ao buscar ranking completo:', err);
        setError('Erro ao conectar com o servidor.');
      } finally {
        setLoading(false);
      }
    };

    if (eventId) {
      fetchRanking();
    }
  }, [eventId, year]);

  // Update current gender when eventId changes or availableEvents loads
  useEffect(() => {
    if (availableEvents.male.length > 0 || availableEvents.female.length > 0) {
      const id = parseInt(eventId);
      const isMale = availableEvents.male.some(e => e.id === id);
      const isFemale = availableEvents.female.some(e => e.id === id);
      
      if (isMale) setCurrentGender('male');
      else if (isFemale) setCurrentGender('female');
    }
  }, [eventId, availableEvents]);

  // Apply filters
  useEffect(() => {
    let result = rankingData;

    if (filters.club !== 'all') {
      result = result.filter(item => item.club_name === filters.club);
    }
    if (filters.nationality !== 'all') {
      result = result.filter(item => item.nationality === filters.nationality);
    }
    if (filters.competition !== 'all') {
      result = result.filter(item => item.competition_name === filters.competition);
    }

    setFilteredData(result);
  }, [filters, rankingData]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleNavigationChange = (type, value) => {
    if (type === 'gender') {
      setCurrentGender(value);
      // When switching gender, try to find the equivalent event (same distance/stroke)
      // or default to the first event of that gender
      const currentEvent = availableEvents[currentGender].find(e => e.id === parseInt(eventId));
      if (currentEvent) {
        const targetList = availableEvents[value];
        const equivalent = targetList.find(e => e.name === currentEvent.name && e.distance === currentEvent.distance);
        if (equivalent) {
          navigate(`/stats/ranking/${equivalent.id}?year=${year}`);
          return;
        }
        // Fallback to first event
        if (targetList.length > 0) {
          navigate(`/stats/ranking/${targetList[0].id}?year=${year}`);
        }
      }
    } else if (type === 'event') {
      navigate(`/stats/ranking/${value}?year=${year}`);
    }
  };

  // Helper to get event name from the first item or fallback
  const getEventTitle = () => {
    if (rankingData.length > 0 && rankingData[0].event_name) {
        const r = rankingData[0];
        return `Ranking ${r.distance_meters}m ${r.stroke} - ${year}`;
    }
    return `Ranking - ${year}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className='bg-white border-b-2 shadow-sm' style={{ borderColor: BORDER }}>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
          <div className="mb-4">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
              Voltar
            </button>
          </div>

          <div className='flex items-center gap-4'>
            <img 
              src="/vukz-logo.jpeg" 
              alt="Arena Stats"
              className="h-12 lg:h-14 w-auto object-contain rounded-xl border-2 p-2"
              style={{ borderColor: BRAND_GOLD }}
            />
            <div>
              <h1 className='text-2xl sm:text-3xl font-extrabold mb-1' style={{ color: BRAND_GREEN }}>
                Ranking Arena
              </h1>
              <p className='text-sm text-gray-600'>
                {getEventTitle()}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation & Filters Section */}
        {!loading && !error && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
            
            {/* Navigation (Event/Gender) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 pb-6 border-b border-gray-100">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Gênero</label>
                <select
                  value={currentGender}
                  onChange={(e) => handleNavigationChange('gender', e.target.value)}
                  className="w-full rounded-lg border-gray-300 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="male">Masculino</option>
                  <option value="female">Feminino</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Prova</label>
                <select
                  value={eventId}
                  onChange={(e) => handleNavigationChange('event', e.target.value)}
                  className="w-full rounded-lg border-gray-300 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  {availableEvents[currentGender]?.map(event => (
                    <option key={event.id} value={event.id}>{event.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: BRAND_GREEN }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <h3 className="font-bold text-sm uppercase tracking-wide" style={{ color: BRAND_GREEN }}>Filtros</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Club Filter */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Clube</label>
                <select
                  value={filters.club}
                  onChange={(e) => handleFilterChange('club', e.target.value)}
                  className="w-full rounded-lg border-gray-300 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="all">Todos os Clubes</option>
                  {uniqueValues.clubs.map(club => (
                    <option key={club} value={club}>{club}</option>
                  ))}
                </select>
              </div>

              {/* Nationality Filter */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Nacionalidade</label>
                <select
                  value={filters.nationality}
                  onChange={(e) => handleFilterChange('nationality', e.target.value)}
                  className="w-full rounded-lg border-gray-300 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="all">Todas as Nacionalidades</option>
                  {uniqueValues.nationalities.map(nat => (
                    <option key={nat} value={nat}>{nat}</option>
                  ))}
                </select>
              </div>

              {/* Competition Filter */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Competição</label>
                <select
                  value={filters.competition}
                  onChange={(e) => handleFilterChange('competition', e.target.value)}
                  className="w-full rounded-lg border-gray-300 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="all">Todas as Competições</option>
                  {uniqueValues.competitions.map(comp => (
                    <option key={comp} value={comp}>{comp}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Active Filters Summary */}
            {(filters.club !== 'all' || filters.nationality !== 'all' || filters.competition !== 'all') && (
              <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center">
                <span className="text-xs text-gray-500">
                  Mostrando <b>{filteredData.length}</b> resultados
                </span>
                <button
                  onClick={() => setFilters({ club: 'all', nationality: 'all', competition: 'all' })}
                  className="text-xs font-bold text-red-600 hover:text-red-800"
                >
                  Limpar Filtros
                </button>
              </div>
            )}
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: BRAND_GREEN }}></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
            {error}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-16">
                      Pos
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Atleta
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Clube
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Tempo
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      Data / Competição
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm border"
                          style={{
                            backgroundColor: 'white',
                            borderColor: row.rank_position === 1 ? BRAND_GOLD : 
                                       row.rank_position === 2 ? '#C0C0C0' : 
                                       row.rank_position === 3 ? '#CD7F32' : 
                                       BRAND_GREEN,
                            color: row.rank_position === 1 ? BRAND_GOLD : 
                                   row.rank_position === 2 ? '#C0C0C0' : 
                                   row.rank_position === 3 ? '#CD7F32' : 
                                   BRAND_GREEN
                          }}
                        >
                          {row.rank_position}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-bold text-gray-900">{row.swimmer_name}</div>
                            <div className="text-xs text-gray-500">{row.nationality}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{row.club_name || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-bold font-mono" style={{ color: BRAND_GREEN }}>
                          {row.time_formatted}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right hidden md:table-cell">
                        <div className="text-xs text-gray-500">{row.competition_name}</div>
                        <div className="text-xs text-gray-400">{row.competition_year}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RankingDetailsPage;
